import React from "react";
import { IMAGES, PROPERTY_FACTORS, DESIGN_SCOPE, IDEAL_CLIENT } from "../mock";
import { ButtonLink, Kicker, SectionHeading } from "../components/UI";
import { Check } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { useSiteImages } from "../context/SiteImages";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import findGreen from "../assets/find-green.png";
import findBlue from "../assets/find-blue.png";

function PageHero({ kicker, title, sub, image, imageKey, cta }) {
  const { simg } = useSiteImages();
  const resolved = imageKey ? simg(imageKey, image) : image;
  return (
    <section className="relative min-h-[80vh] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img src={resolved} alt={typeof title === "string" ? title : "KRS"} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/35 to-navy/45" />
      </div>
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 md:pb-24">
        <div className="max-w-3xl">
          <Kicker dark>{kicker}</Kicker>
          <h1 className="font-serif font-medium text-cream text-4xl md:text-6xl lg:text-[4.4rem] leading-[1.05] mt-5">{title}</h1>
          {sub && <p className="font-sans text-cream/85 text-lg mt-6 max-w-2xl font-light">{sub}</p>}
          {cta && <div className="mt-9">{cta}</div>}
        </div>
      </div>
    </section>
  );
}

export default function FindRenovate() {
  const ref = useReveal();
  const { simg } = useSiteImages();
  return (
    <div ref={ref}>
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <BeforeAfterSlider before={findGreen} after={findBlue} beforeLabel="" afterLabel="" className="w-full h-full min-h-[80vh]" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-navy/40 pointer-events-none" />
        </div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 md:pb-24 pointer-events-none">
          <div className="max-w-3xl">
            <Kicker dark>Find + Renovate</Kicker>
            <h1 className="font-serif font-medium text-cream text-4xl md:text-6xl lg:text-[4.4rem] leading-[1.05] mt-5">Don't Just Search for the Perfect Home. Find a Home You Can <span className="text-gold-soft italic">Make Perfect.</span></h1>
            <p className="font-sans text-cream/85 text-lg mt-6 max-w-2xl font-light">We help buyers identify fixer-uppers and homes with renovation potential, then bring together the design and renovation expertise to turn that property into the home they actually want.</p>
            <div className="mt-9 pointer-events-auto inline-block"><ButtonLink to="/contact" variant="gold">Start My Home Search</ButtonLink></div>
            <p className="mt-6 font-sans text-[11px] tracking-luxe uppercase text-cream/60">Drag to see the transformation</p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <SectionHeading kicker="A New Perspective" title="Stop Judging Homes by Their Current Condition." className="reveal" />
          <div className="reveal space-y-5 mt-8 font-sans text-lg text-charcoal/70 leading-relaxed font-light">
            <p>Most buyers walk into a dated home and immediately see everything that's wrong with it. We see opportunity.</p>
            <p>That outdated kitchen? It may become a beautiful open entertaining space. The awkward floor plan? There may be an opportunity to completely rethink it. The unattractive finishes? Those are temporary.</p>
          </div>
          <div className="reveal mt-10 border-l-2 border-gold pl-8">
            <p className="font-serif text-2xl md:text-3xl text-navy leading-snug">You're no longer simply asking <span className="italic text-charcoal/50">“Do I like this house?”</span> — you're asking <span className="italic text-gold">“Could this become the house I want?”</span></p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream-dark">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <SectionHeading kicker="Finding The Right Property" title="We Look at More Than Bedrooms and Bathrooms." className="reveal" />
            <p className="reveal font-sans text-charcoal/70 leading-relaxed mt-6 font-light text-lg">The goal isn't simply to find a property. It's to find a property that makes sense <span className="italic text-navy">before and after the renovation.</span></p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 reveal grid grid-cols-2 gap-x-6 gap-y-4">
            {PROPERTY_FACTORS.map((f) => (
              <div key={f} className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /><span className="font-sans text-sm text-charcoal/80">{f}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 img-zoom reveal order-2 lg:order-1">
            <img src={simg("find_design", IMAGES.materials1)} alt="Design materials" className="w-full h-[520px] object-cover" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionHeading kicker="Design Before You Renovate" title="Imagine the Possibilities." className="reveal" />
            <p className="reveal font-sans text-charcoal/70 leading-relaxed mt-6 font-light text-lg">Once you've found the right home, we help translate your ideas into a clear design direction.</p>
            <div className="reveal grid grid-cols-2 gap-x-6 gap-y-3 mt-8">
              {DESIGN_SCOPE.map((d) => (
                <div key={d} className="flex items-center gap-3"><span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /><span className="font-sans text-sm text-charcoal/80">{d}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <SectionHeading dark kicker="Renovation" title="From Fixer-Upper to Finished Home." center className="reveal" />
          <p className="reveal font-sans text-cream/75 text-lg mt-8 font-light leading-relaxed">Instead of buying a property and then starting from scratch to find designers and contractors, you already have a team that understands the vision.</p>
          <p className="reveal font-serif italic text-gold-soft text-3xl mt-8">One process. One direction. One goal: turn the home you bought into the home you imagined.</p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <SectionHeading kicker="Ideal Client" title="This Service Is Perfect If…" className="reveal mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
            {IDEAL_CLIENT.map((c) => (
              <div key={c} className="reveal flex items-start gap-4 border-b border-cream-dark pb-6">
                <span className="w-8 h-8 border border-gold flex items-center justify-center shrink-0 mt-0.5"><Check size={16} className="text-gold" strokeWidth={1.5} /></span>
                <span className="font-sans text-charcoal/80 leading-relaxed">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-cream-dark text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="reveal font-serif font-medium text-navy text-4xl md:text-5xl leading-tight">Your Dream Home May Already Be on the Market.</h2>
          <p className="reveal font-sans text-charcoal/60 text-lg mt-5 font-light">It just might not look like your dream home yet.</p>
          <div className="reveal flex justify-center mt-9"><ButtonLink to="/contact" variant="navy">Let's Find It</ButtonLink></div>
        </div>
      </section>
    </div>
  );
}

export { PageHero };
